import { Button, Dialog, DialogContent, DialogTitle, Divider } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../api/adminApi";
import type { EntityFieldType } from "../../../../../api/types/objectTypes";
import WorkflowTemplateDetailsContext from "../WorkflowTemplateDetailsContext";
import WorkflowTemplateActivityFieldForm from "./WorkflowTemplateActivityFieldForm";
import {
  type WorkflowTemplateActivityFieldFormState,
  type WorkflowTemplateActivityFieldFormTouchedFields,
  validateWorkflowTemplateActivityFieldForm,
} from "./workflowTemplateActivityFieldFormState";

interface Props {
  activityId: string;
  onClose: () => void;
}

const updateActivityInWorkflowTemplate = withErrorHandling(adminApi.updateActivityInWorkflowTemplate);
const allowedWorkflowTemplateActivityFieldTypes: EntityFieldType[] = [
  "Text",
  "Number",
  "Money",
  "Percent",
  "Date",
  "Checkbox",
];

const AddWorkflowTemplateActivityFieldDialog = ({ activityId, onClose }: Props) => {
  const { workflowTemplate, onActivityUpdated } = useDefinedContext(WorkflowTemplateDetailsContext);
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [saving, setSaving] = useState(false);
  const [touchedFields, setTouchedFields] = useState<WorkflowTemplateActivityFieldFormTouchedFields>({});
  const [formState, setFormState] = useState<WorkflowTemplateActivityFieldFormState>({
    displayName: "",
    type: "Text",
    description: "",
    isRequired: false,
  });

  const handleSave = async () => {
    setSaving(true);
    const [updatedActivity, error] = await updateActivityInWorkflowTemplate(workflowTemplate.id, activityId, {
      outputFieldSchema: {
        newFields: [
          {
            displayName: formState.displayName.trim(),
            type: formState.type,
            descriptionUserInput: formState.description.trim(),
            attributes: formState.isRequired ? ["Required"] : undefined,
          },
        ],
      },
      updateActiveWorkflows: false,
    });
    setSaving(false);

    if (error) {
      logError(error, "[AddWorkflowTemplateActivityFieldDialog] updateActivityInWorkflowTemplate");
      sendNotificationError(error.message);
      return;
    }

    onActivityUpdated(updatedActivity);
    sendNotification("Field added successfully");
    onClose();
  };

  const validationResults = validateWorkflowTemplateActivityFieldForm(formState);

  return (
    <Dialog maxWidth="sm" fullWidth open onClose={onClose}>
      <DialogTitle>Add Field</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <Divider />

      <DialogContent>
        <WorkflowTemplateActivityFieldForm
          formState={formState}
          validationResults={validationResults}
          touchedFields={touchedFields}
          allowedFieldTypes={allowedWorkflowTemplateActivityFieldTypes}
          onChange={setFormState}
          onFieldTouched={(field) => setTouchedFields((prev) => ({ ...prev, [field]: true }))}
        />
      </DialogContent>

      <Divider />
      <DialogButtons onCancel={onClose}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          loading={saving}
          disabled={!validationResults.isValid}
        >
          Add
        </Button>
      </DialogButtons>
    </Dialog>
  );
};

export default AddWorkflowTemplateActivityFieldDialog;
